'use client';

import { useState, useEffect, useMemo } from 'react';

import { useListing } from '../../../../../Contexts/ListingProvider';
import ReviewPublishStepView from './ReviewPublishStep.view';

/**
 * Step 5, review and publish. The container.
 *
 * Owns the object URLs for the preview gallery and the gallery index. It is
 * still a dumb step in the wizard's terms: `handleSubmit` and `isSubmitting`
 * come from `CreateListing`, which owns the one POST this page makes.
 *
 * The old `formatPrice` helper and its four `console.log`/`console.warn` calls
 * are gone. It re-implemented `formatCurrency` badly: an empty price became
 * `R0.00`, which reads as "free" rather than "not set". `formatCurrency`
 * already answers "No price" for a missing value, in words, and that is the
 * honest reading.
 */

export const CONDITION_LABELS = {
  'Near Mint': 'Near Mint (NM)',
  'Lightly Played': 'Lightly Played (LP)',
  'Moderately Played': 'Moderately Played (MP)',
  'Heavily Played': 'Heavily Played (HP)',
  Damaged: 'Damaged (DMG)',
};

const ReviewPublishStep = ({ formData, handleSubmit, isSubmitting, submitError, titleRef }) => {
  const { lookUpData } = useListing();
  const [imageIndex, setImageIndex] = useState(0);

  const files = formData.images || [];

  // One object URL per file, revoked when the set changes. The old step called
  // URL.createObjectURL() straight inside render, which minted a fresh blob URL
  // on every keystroke elsewhere in the wizard and never revoked one.
  const images = useMemo(
    () =>
      files.map((file, index) => ({
        id: index,
        image: typeof file === 'string' ? file : URL.createObjectURL(file),
      })),
    [files]
  );

  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img.image?.startsWith('blob:')) URL.revokeObjectURL(img.image);
      });
    };
  }, [images]);

  useEffect(() => {
    setImageIndex((current) => (current < images.length ? current : 0));
  }, [images.length]);

  return (
    <ReviewPublishStepView
      titleRef={titleRef}
      formData={formData}
      card={lookUpData}
      conditionLabels={CONDITION_LABELS}
      images={images}
      imageIndex={imageIndex}
      onPrevImage={() =>
        setImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
      }
      onNextImage={() =>
        setImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
      }
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      submitError={submitError}
    />
  );
};

export default ReviewPublishStep;
